// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import {
  DefaultApi,
  ExercisedTreeEvent,
  Filters,
  JsContractEntry,
  JsContractEntryOneOf,
  TransactionFilter,
  DisclosedContract,
} from '@digitalasset/json-api-ts-client';
import { ObjectSerializer } from '@digitalasset/json-api-ts-client/models/ObjectSerializer';
import hash from 'object-hash';
import { v4 as uuidv4 } from 'uuid';
import { BurnMintFactory } from '@daml.js/splice-api-token-burn-mint-v1-1.0.0/lib/Splice/Api/Token/BurnMintV1';
import {
  Holding as TokenStandardHolding,
  InstrumentId,
} from '@daml.js/splice-api-token-holding-v1-1.0.0/lib/Splice/Api/Token/HoldingV1';
import {
  BridgeAgreement,
  BridgeAgreementOffer,
} from '@daml.js/utility-bridge-v0/lib/Utility/Bridge/V0/Agreement/Bridge';
import {
  BridgeUserAgreement,
  BridgeUserAgreementRequest,
  RejectedBridgeUserAgreementRequest,
} from '@daml.js/utility-bridge-v0/lib/Utility/Bridge/V0/Agreement/User';
import { ContextContractIds } from '@daml.js/utility-bridge-v0/lib/Utility/Bridge/V0/ApiUtil';
import { DepositAttestation } from '@daml.js/utility-bridge-v0/lib/Utility/Bridge/V0/Attestation/Deposit';
import {
  AcceptedWithdrawAttestation,
  WithdrawAttestation,
} from '@daml.js/utility-bridge-v0/lib/Utility/Bridge/V0/Attestation/Withdraw';
import { NonceConfiguration, StoredNonces } from '@daml.js/utility-bridge-v0/lib/Utility/Bridge/V0/Nonce/Nonce';
import { BridgeConfiguration } from '@daml.js/utility-bridge-v0/lib/Utility/Bridge/V0/Util';
import { BridgeService, BridgeServiceRequest } from '@daml.js/utility-bridge-app-v0/lib/Utility/Bridge/App/V0/Service/Bridge';
import InterfaceContract from './InterfaceContract';
import TemplateContract from './TemplateContract';
import { ACSHelper } from './ACSHelper';
import {
  Choice,
  ContractId,
  InterfaceCompanion,
  Party,
  Template,
  DisclosedContract as DamlDisclosedContract
} from '@daml/types';
import {
  CommandMeta,
  CreateEvent
} from '@daml/ledger';
import { InstrumentConfiguration } from '@daml.js/utility-registry-v0/lib/Utility/Registry/V0/Configuration/Instrument';
import {
  AppRewardConfiguration,
} from '@daml.js/utility-registry-v0/lib/Utility/Registry/V0/Configuration/AppReward';
import { AllocationFactory } from '@daml.js/utility-registry-app-v0/lib/Utility/Registry/App/V0/Service/AllocationFactory';
import { Holding as RegistryHolding } from '@daml.js/utility-registry-holding-v0/lib/Utility/Registry/Holding/V0/Holding';

type SubmitterArgs =
  | {
      actAs?: Party[];
      readAs?: Party[];
      submitter?: never;
    }
  | {
      actAs?: never;
      readAs?: never;
      submitter?: Party;
    };

export type LedgerCreateArgs<T extends object> = {
  argument: T;
  meta?: CommandMeta;
} & SubmitterArgs;

export type LedgerExerciseArgs<T extends object, C> = {
  cid: ContractId<T>;
  argument: C;
  meta?: CommandMeta;
} & SubmitterArgs;

// Uses the JSON API (via @daml/ledger) to connect to the ledger.
export default class LedgerApiClientV2 {
  private apiInstance: DefaultApi;

  private userId: string;

  private defaultSubmitter: string;

  private deduplicationDurationInSeconds: number;

  // needs to be marked with # in order to avoid serialization (LedgerApiClientV2 is serialized when used as key)
  #acsHelper: ACSHelper;

  constructor(
    apiInstance: DefaultApi,
    userId: string,
    defaultParty: string,
    deduplicationDurationInSeconds: number,
    acsHelper: ACSHelper,
  ) {
    this.apiInstance = apiInstance;
    this.userId = userId;
    this.deduplicationDurationInSeconds = deduplicationDurationInSeconds;
    this.#acsHelper = acsHelper;

    this.defaultSubmitter = defaultParty;

    // Disable the validation step during the serialization. This is a temporary workaround to a bug in the current codegen.
    // eslint-disable-next-line func-names, @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars
    ObjectSerializer.serialize = function (data: any, _type: any, _format: any) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return data;
    };
    // eslint-disable-next-line func-names, @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars
    ObjectSerializer.deserialize = function (data: any, _type: any, _format: any) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return data;
    };
  }

  setDefaultSubmitter(party: string): void {
    this.defaultSubmitter = party;
  }

  api() {
    return this.apiInstance;
  }

  async acceptBridgeAgreementOffer(
    bridgeAgreementOfferCid: ContractId<BridgeAgreementOffer>,
    nonceConfig: NonceConfiguration,
    bridgeConfig: BridgeConfiguration,
    withdrawalAttester: string,
  ): Promise<ContractId<BridgeAgreement>> {
    return this.exercise(
      BridgeAgreementOffer.BridgeAgreementOffer_Accept,
      bridgeAgreementOfferCid,
      {
        nonceConfig,
        bridgeConfig,
        withdrawalAttester,
      },
    ).then((res) => res[0].bridgeAgreementCid);
  }

  async acceptBridgeServiceRequest(
    bridgeServiceRequestCid: ContractId<BridgeServiceRequest>,
  ): Promise<ContractId<BridgeService>> {
    return this.exercise(
      BridgeServiceRequest.BridgeServiceRequest_Accept,
      bridgeServiceRequestCid,
      {},
    ).then((res) => res[0].bridgeServiceCid);
  }

  async createDepositAttestation(
    bridgeAgreementCid: ContractId<BridgeAgreement>,
    recipient: string,
    payload: string,
    messageHash: string,
    attestation: string,
    reference: string,
    usedNoncesCid: ContractId<StoredNonces>,
  ): Promise<[ContractId<DepositAttestation>, string]> {
    return this.exercise(
      BridgeAgreement.BridgeAgreement_CreateDepositAttestation,
      bridgeAgreementCid,
      {
        recipient,
        payload,
        messageHash,
        attestation,
        reference,
        usedNoncesCid,
      },
    ).then((res) => [res[0].depositAttestationCid, res[1]]);
  }

  async acceptBridgeUserAgreement(
    bridgeAgreementCid: ContractId<BridgeAgreement>,
    bridgeUserAgreementRequestCid: ContractId<BridgeUserAgreementRequest>,
  ): Promise<ContractId<BridgeUserAgreement>> {
    return this.exercise(
      BridgeAgreement.BridgeAgreement_AcceptBridgeUserAgreement,
      bridgeAgreementCid,
      { cid: bridgeUserAgreementRequestCid },
    ).then((res) => res[0].bridgeUserAgreementCid);
  }

  async rejectBridgeUserAgreement(
    bridgeAgreementCid: ContractId<BridgeAgreement>,
    bridgeUserAgreementRequestCid: ContractId<BridgeUserAgreementRequest>,
    reason: string,
  ): Promise<ContractId<RejectedBridgeUserAgreementRequest>> {
    return this.exercise(
      BridgeAgreement.BridgeAgreement_RejectBridgeUserAgreement,
      bridgeAgreementCid,
      {
        cid: bridgeUserAgreementRequestCid,
        payload: {
          reason,
        },
      },
    ).then((res) => res[0].rejectedBridgeUserAgreementRequestCid);
  }

  async requestBridgeUserAgreement(
    crossChainRepresentative: string,
    operator: string,
    bridgeOperator: string,
    user: string,
    instrumentId: InstrumentId,
  ): Promise<CreateEvent<BridgeUserAgreementRequest>> {
    return this.create(BridgeUserAgreementRequest, {
      crossChainRepresentative,
      operator,
      bridgeOperator,
      user,
      instrumentId,
      preApproval: false, //TODO Once implemented, this should be an argument to the function
    });
  }

  async terminateBridgeUserAgreement(
    bridgeUserAgreementCid: ContractId<BridgeUserAgreement>,
    reason: string,
    actor: string,
  ): Promise<void> {
    await this.exercise(BridgeUserAgreement.BridgeUserAgreement_Terminate, bridgeUserAgreementCid, {
      reason,
      actor,
    });
  }

  async mintFromAttestation(
    bridgeUserAgreementCid: ContractId<BridgeUserAgreement>,
    depositAttestationCid: ContractId<DepositAttestation>,
    factoryCid: ContractId<BurnMintFactory>,
    contextContractIds: ContextContractIds,
    disclosedContracts: DamlDisclosedContract[],
  ): Promise<[ContractId<TokenStandardHolding>, string]> {
    return this.exercise(
      BridgeUserAgreement.BridgeUserAgreement_Mint,
      bridgeUserAgreementCid,
      {
        depositAttestationCid,
        factoryCid,
        contextContractIds,
      },
      { disclosedContracts },
    ).then((res) => [res[0].holdingCid, res[1]]);
  }

  async burnAndAttest(
    bridgeUserAgreementCid: ContractId<BridgeUserAgreement>,
    amount: string,
    destinationDomain: string,
    destinationRecipient: string,
    holdingCids: ContractId<TokenStandardHolding>[],
    reference: string,
    factoryCid: ContractId<BurnMintFactory>,
    contextContractIds: ContextContractIds,
    requestId: string,
    disclosedContracts: DamlDisclosedContract[],
  ): Promise<[ContractId<WithdrawAttestation>, string]> {
    return this.exercise(
      BridgeUserAgreement.BridgeUserAgreement_Burn,
      bridgeUserAgreementCid,
      {
        amount,
        destinationDomain,
        destinationRecipient,
        holdingCids,
        reference,
        factoryCid,
        contextContractIds,
        requestId,
      },
      { disclosedContracts },
    ).then((res) => [res[0].withdrawAttestationCid, res[1]]);
  }

  async queryHoldings(offset: number): Promise<TemplateContract<RegistryHolding>[]> {
    const res = await this.query(offset, RegistryHolding);
    return res;
  }

  async queryBridgeHoldings(
    offset: number,
    crossChainRepresentative: string,
    holder: string,
  ): Promise<TemplateContract<RegistryHolding>[]> {
    const res = await this.query(offset, RegistryHolding);
    return res.filter((holding) => holding.payload.registrar === crossChainRepresentative && holding.payload.owner === holder);
  }

  async queryTokenStandardHoldings(
    offset: number,
  ): Promise<InterfaceContract<TokenStandardHolding>[]> {
    return this.queryInterface(offset, TokenStandardHolding);
  }

  async queryBridgeAgreement(
    offset: number,
    bridgeOperator: string,
  ): Promise<TemplateContract<BridgeAgreement> | null> {
    const ba = await this.query(offset, BridgeAgreement);
    return ba.find((r) => r.payload.bridgeOperator === bridgeOperator) || null;
  }

  async queryBridgeService(
    offset: number,
    bridgeOperator: string,
  ): Promise<TemplateContract<BridgeService> | null> {
    const ba = await this.query(offset, BridgeService);
    return ba.find((r) => r.payload.bridgeOperator === bridgeOperator) || null;
  }

  async queryBridgeAgreementOffer(
    offset: number,
    bridgeOperator: string,
  ): Promise<TemplateContract<BridgeAgreementOffer> | null> {
    const bao = await this.query(offset, BridgeAgreementOffer);
    return bao.find((r) => r.payload.bridgeOperator === bridgeOperator) || null;
  }

  async queryBridgeServiceRequest(
    offset: number,
    bridgeOperator: string,
  ): Promise<TemplateContract<BridgeServiceRequest> | null> {
    const bao = await this.query(offset, BridgeServiceRequest);
    return bao.find((r) => r.payload.bridgeOperator === bridgeOperator) || null;
  }

  async queryBridgeUserAgreements(
    offset: number,
    bridgeOperator: string,
  ): Promise<TemplateContract<BridgeUserAgreement>[]> {
    const bua = await this.query(offset, BridgeUserAgreement);
    return bua.filter((r) => r.payload.bridgeOperator === bridgeOperator);
  }

  async queryBridgeUserAgreement(
    offset: number,
    bridgeOperator: string,
    user: string,
  ): Promise<TemplateContract<BridgeUserAgreement> | null> {
    const bua = await this.query(offset, BridgeUserAgreement);
    return (
      bua.find((r) => r.payload.bridgeOperator === bridgeOperator && r.payload.user === user) ||
      null
    );
  }

  async queryBridgeUserAgreementRequests(
    offset: number,
    bridgeOperator: string,
  ): Promise<TemplateContract<BridgeUserAgreementRequest>[]> {
    const buar = await this.query(offset, BridgeUserAgreementRequest);
    return buar.filter((r) => r.payload.bridgeOperator === bridgeOperator);
  }

  // Helper to convert a string to a ValidParty { tag: 'Valid', value: string }
  private toValidParty(party: string): { tag: 'Valid'; value: string } {
    return { tag: 'Valid', value: party };
  }

  async queryUserWithdrawalAttestations(
    offset: number,
    bridgeOperator: string,
    user: string,
  ): Promise<TemplateContract<WithdrawAttestation>[]> {
    const att = await this.query(offset, WithdrawAttestation);
    return att.filter(
      (r) =>
        r.payload.bridgeOperator === bridgeOperator &&
        r.payload.sourceDepositor === this.toValidParty(user),
    );
  }

  async queryUserAcceptedWithdrawalAttestations(
    offset: number,
    bridgeOperator: string,
    user: string,
  ): Promise<TemplateContract<AcceptedWithdrawAttestation>[]> {
    const att = await this.query(offset, AcceptedWithdrawAttestation);
    return att.filter(
      (r) =>
        r.payload.attestation.bridgeOperator === bridgeOperator &&
        r.payload.attestation.sourceDepositor === this.toValidParty(user),
    );
  }

  async queryUserDepositAttestations(
    offset: number,
    bridgeOperator: string,
    user: string,
  ): Promise<TemplateContract<DepositAttestation>[]> {
    const att = await this.query(offset, DepositAttestation);
    return att.filter(
      (r) => r.payload.bridgeOperator === bridgeOperator && r.payload.recipient === user,
    );
  }

  async queryWithdrawalAttestations(
    offset: number,
    bridgeOperator: string,
  ): Promise<TemplateContract<WithdrawAttestation>[]> {
    const att = await this.query(offset, WithdrawAttestation);
    return att.filter((r) => r.payload.bridgeOperator === bridgeOperator);
  }

  async queryAcceptedWithdrawalAttestations(
    offset: number,
    bridgeOperator: string,
  ): Promise<TemplateContract<AcceptedWithdrawAttestation>[]> {
    const att = await this.query(offset, AcceptedWithdrawAttestation);
    return att.filter((r) => r.payload.attestation.bridgeOperator === bridgeOperator);
  }

  async queryDepositAttestations(
    offset: number,
    bridgeOperator: string,
  ): Promise<TemplateContract<DepositAttestation>[]> {
    const att = await this.query(offset, DepositAttestation);
    return att.filter((r) => r.payload.bridgeOperator === bridgeOperator);
  }

  async queryBridgeInstrumentConfigurations(
    offset: number,
    bridgeOperator: string,
  ): Promise<TemplateContract<InstrumentConfiguration>[]> {
    const res = await this.queryWithBlob(offset, InstrumentConfiguration);
    return res.filter((r) => r.payload.provider === bridgeOperator);
  }

  async queryBridgeInstrumentConfiguration(
    offset: number,
    bridgeOperator: string,
    registrar: string,
  ): Promise<TemplateContract<InstrumentConfiguration> | null> {
    const res = await this.queryWithBlob(offset, InstrumentConfiguration);
    return (
      res.find((r) => r.payload.provider === bridgeOperator && r.payload.registrar === registrar) ||
      null
    );
  }

  async queryAppRewardConfigurations(
    offset: number,
  ): Promise<TemplateContract<AppRewardConfiguration>[]> {
    const configs = await this.queryWithBlob(offset, AppRewardConfiguration);
    return configs;
  }

  async queryBridgeFactories(
    offset: number,
    bridgeOperator: string,
  ): Promise<TemplateContract<AllocationFactory>[]> {
    const res = await this.queryWithBlob(offset, AllocationFactory);
    return res.filter((r) => r.payload.provider === bridgeOperator);
  }

  async queryBridgeFactory(
    offset: number,
    bridgeOperator: string,
    registrar: string,
  ): Promise<TemplateContract<AllocationFactory> | null> {
    const res = await this.queryWithBlob(offset, AllocationFactory);
    return (
      res.find((r) => r.payload.provider === bridgeOperator && r.payload.registrar === registrar) ||
      null
    );
  }

  async queryBridgeStoredNonces(
    offset: number,
    bridgeOperator: string,
    nonce: string,
  ): Promise<TemplateContract<StoredNonces> | null> {
    const res = await this.query(offset, StoredNonces);
    return res.find(
      (storedNonce) =>
        storedNonce.payload.bridgeOperator === bridgeOperator &&
        nonce.startsWith(storedNonce.payload.prefix)
    ) || null;
  }

  async getLedgerEnd(): Promise<number> {
    return this.apiInstance.getV2StateLedgerEnd().then((r) => r.offset);
  }

  async create<T extends object, K, I extends string>(
    template: Template<T, K, I>,
    argument: T,
    meta?: CommandMeta,
    actAs: string[] = this.defaultSubmitter ? [this.defaultSubmitter] : [],
    readAs: string[] = [],
  ): Promise<CreateEvent<T, K, I>> {
    const commandId = hash({
      templateId: template.templateId,
      argument,
      actAs,
      readAs,
    });

    const command = {
      CreateCommand: {
        templateId: template.templateId,
        createArguments: template.encode(argument),
      },
    };

    const commands = {
      commands: {
        commands: [command],
        workflowId: meta?.workflowId || '',
        userId: this.userId,
        commandId,
        deduplicationPeriod: this.getDeduplicationDuration(),
        actAs,
        readAs,
        submissionId: uuidv4(),
        disclosedContracts: LedgerApiClientV2.toDisclosedContracts(meta),
        domainId: meta?.domainId || '',
        packageIdSelectionPreference: [],
      },
      transactionFormat: LedgerApiClientV2.getTransactionFormat(actAs),
    };

    const response = await this.apiInstance.postV2CommandsSubmitAndWaitForTransaction(commands);

    const event = response.transaction.events?.at(0);
    if (event && 'CreatedEvent' in event) {
      const createdEvent = event.CreatedEvent;
      return {
        templateId: template.templateId,
        contractId: createdEvent.contractId as ContractId<T>,
        signatories: createdEvent.signatories || [],
        observers: createdEvent.observers || [],
        key: createdEvent.contractKey as K,
        payload: template.decoder.runWithException(createdEvent.createArgument),
      };
    }
    return Promise.reject(new Error('CreatedEvent is expected from the response'));
  }

  private async queryInterface<T extends object, K, I extends string>(
    offset: number,
    interfaceCompanion: InterfaceCompanion<T, K, I>,
    party: string | undefined = this.defaultSubmitter,
  ): Promise<InterfaceContract<T>[]> {
    return this.activeContractsForInterface(offset, party, interfaceCompanion.templateId).then(
      (responses) => {
        return responses
          .map((resp) => resp.contractEntry)
          .filter(LedgerApiClientV2.isJsActiveContract)
          .map((activeContract) =>
            InterfaceContract.fromJsonAPIV2(
              interfaceCompanion,
              activeContract.JsActiveContract.createdEvent,
            ),
          );
      },
    );
  }

  async exercise<T extends object, C, R, K>(
    choice: Choice<T, C, R, K>,
    cid: ContractId<T>,
    argument: C,
    meta?: CommandMeta,
    actAs: string[] = this.defaultSubmitter ? [this.defaultSubmitter] : [],
    readAs: string[] = [],
  ): Promise<[R, string]> {
    const commandId = hash({
      contractId: cid,
      templateId: choice.template().templateId,
      choiceName: choice.choiceName,
      argument,
      actAs,
      readAs,
    });

    const command = {
      ExerciseCommand: {
        templateId: choice.template().templateId,
        contractId: cid,
        choice: choice.choiceName,
        choiceArgument: choice.argumentEncode(argument),
      },
    };

    const commands = {
      commands: [command],
      workflowId: meta?.workflowId || '',
      userId: this.userId,
      commandId,
      deduplicationPeriod: this.getDeduplicationDuration(),
      actAs,
      readAs,
      submissionId: uuidv4(),
      disclosedContracts: LedgerApiClientV2.toDisclosedContracts(meta),
      domainId: meta?.domainId || '',
      packageIdSelectionPreference: [],
    };

    const response = await this.apiInstance.postV2CommandsSubmitAndWaitForTransactionTree(commands);
    const tree = response.transactionTree;
    const eventIds = Object.keys(tree.eventsById).map(Number);
    const rootEventId = Math.min(...eventIds);
    const rootEvent = tree.eventsById[rootEventId];

    if (rootEvent && 'ExercisedTreeEvent' in rootEvent) {
      const exercisedEvent: ExercisedTreeEvent = rootEvent.ExercisedTreeEvent as ExercisedTreeEvent;
      if ('value' in exercisedEvent && 'exerciseResult' in exercisedEvent.value) {
        const result = choice.resultDecoder.runWithException(exercisedEvent.value.exerciseResult);
        return [result, tree.updateId] as [R, string];
      }
    }

    return Promise.reject(new Error('ExercisedTreeEvent is expected from the response'));
  }

  private async query<T extends object, K, I extends string>(
    offset: number,
    template: Template<T, K, I>,
    party: string = this.defaultSubmitter,
  ): Promise<TemplateContract<T>[]> {
    return this.activeContractsForTemplate(offset, party, template.templateId).then((responses) => {
      return responses
        .map((resp) => resp.contractEntry)
        .filter(LedgerApiClientV2.isJsActiveContract)
        .map((activeContract) =>
          TemplateContract.fromJsonAPIV2(template, activeContract.JsActiveContract.createdEvent),
        );
    });
  }

  private async queryWithBlob<T extends object, K, I extends string>(
    offset: number,
    template: Template<T, K, I>,
    party: string = this.defaultSubmitter,
  ): Promise<TemplateContract<T>[]> {
    return this.activeContractsForTemplateWithBlob(offset, party, template.templateId).then((responses) => {
      return responses
        .map((resp) => resp.contractEntry)
        .filter(LedgerApiClientV2.isJsActiveContract)
        .map((activeContract) =>
          TemplateContract.fromJsonAPIV2(template, activeContract.JsActiveContract.createdEvent),
        );
    });
  }

  private static isJsActiveContract = (
    contract: JsContractEntry,
  ): contract is JsContractEntryOneOf => {
    return 'JsActiveContract' in contract;
  };

  async activeContractsForTemplate(offset: number, partyFilter: string, templateId: string) {
    return this.#acsHelper.activeContractsForTemplate(offset, partyFilter, templateId);
  }

  async activeContractsForTemplateWithBlob(offset: number, partyFilter: string, templateId: string) {
    // Bypass cache for blob requests since ACSHelper doesn't support includeCreatedEventBlob: true
    const filters: Filters = {
      cumulative: [
        {
          identifierFilter: {
            TemplateFilter: {
              value: {
                templateId,
                includeCreatedEventBlob: true,
              },
            },
          },
        },
      ],
    };
    return this.activeContracts(filters, offset, partyFilter);
  }

  async activeContractsForInterface(
    offset: number,
    partyFilter: string | undefined,
    interfaceId: string,
  ) {
    return this.#acsHelper.activeContractsForInterface(offset, partyFilter, interfaceId);
  }

  async activeContracts(filters: Filters, offset: number, partyFilter: string | undefined) {
    const filter: TransactionFilter = partyFilter
      ? {
          filtersByParty: {
            [partyFilter]: filters,
          },
        }
      : {
          filtersByParty: {},
          filtersForAnyParty: filters,
        };

    return this.apiInstance.postV2StateActiveContracts({
      filter,
      activeAtOffset: offset,
      verbose: false,
    });
  }

  private static toDisclosedContracts(meta?: CommandMeta, domainId?: string): DisclosedContract[] {
    return (
      meta?.disclosedContracts?.map((disclosedContract) => ({
        ...disclosedContract,
        domainId: domainId || meta?.domainId || '',
        synchronizerId: domainId || meta?.domainId || '',
      })) || []
    );
  }

  private getDeduplicationDuration() {
    return {
      DeduplicationDuration: {
        value: {
          seconds: this.deduplicationDurationInSeconds,
          nanos: 0,
        },
      },
    };
  }

  private static getTransactionFormat(parties: string[]) {
    const filtersByParties = parties.reduce(
      (acc, party) => ({
        ...acc,
        [party]: {
          cumulative: [
            {
              identifierFilter: {
                WildcardFilter: {
                  value: {
                    includeCreatedEventBlob: true,
                  },
                },
              },
            },
          ],
        },
      }),
      {},
    );

    return {
      eventFormat: {
        filtersByParty: filtersByParties,
        verbose: false,
      },
      transactionShape: 'TRANSACTION_SHAPE_ACS_DELTA',
    };
  }
}
